import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { Icon, IconComponent } from '@examdojo/core/icon';

export interface ProgramData {
  code: string;
  short_name: string;
  full_name: string;
  icon: Icon | null;
}

export interface ProgramBadgeConfig {
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

@Component({
  selector: 'exam-cms-program-badge',
  template: `
    <span [class]="badgeClasses()">
      @if (config().showIcon && program().icon) {
        <dojo-icon [icon]="program().icon!" [class]="iconClasses()" />
      }
      <span class="flex-shrink-0">{{ program().code }}</span>
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [IconComponent],
})
export class ProgramBadgeComponent {
  readonly program = input.required<ProgramData>();
  readonly config = input<ProgramBadgeConfig>({});

  readonly badgeClasses = computed(() => {
    const config = this.config();
    const size = config.size || 'md';
    const showIcon = config.showIcon || false;

    const baseClasses = 'inline-flex items-center justify-center rounded-full font-medium whitespace-nowrap';
    const sizeClasses = this.getSizeClasses(size);
    const colorClasses = this.getColorClasses();
    // Fixed height for consistent alignment
    const heightClass = size === 'sm' ? 'h-5' : size === 'lg' ? 'h-7' : 'h-6';
    // Add gap only when icon is present
    const gapClass = showIcon && this.program().icon ? 'gap-1' : '';

    return `${baseClasses} ${sizeClasses} ${colorClasses} ${gapClass} ${heightClass}`.trim();
  });

  readonly iconClasses = computed(() => {
    const config = this.config();
    const size = config.size || 'md';
    // Fixed height for consistent icon sizing
    return size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-4 w-4' : 'h-3.5 w-3.5';
  });

  private getSizeClasses(size: 'sm' | 'md' | 'lg'): string {
    switch (size) {
      case 'sm':
        return 'px-2 py-0.5 text-xs';
      case 'lg':
        return 'px-3 py-1.5 text-sm';
      default:
        return 'px-2 py-1 text-xs';
    }
  }

  private getColorClasses(): string {
    // Special styling for "All" (ADMIN users)
    if (this.program().code === 'All') {
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
    }
    // Use a neutral color scheme for regular programs
    return 'bg-neutral-100 text-gray-800 dark:bg-neutral-800 dark:text-gray-200';
  }
}
